import { Component, OnInit } from '@angular/core';
import { map } from 'rxjs';
import { GraphQLService } from '../../../../services/graph-ql.service';
import { DatabaseQueries, OrderQueries } from '../../../../shared/types/get-query';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ImageService } from '../../../../services/image.service';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { CommonService } from 'src/app/services/common.service';
import { Color, Style, StyleStatus } from '../../models/new/style';
import { StyleMapper } from '../../services/style-mapper';
import { ColorShots } from '../../models/new/order';
import { OrderMapper } from '../../services/order-mapper';
import { Shot, ShotApprovalStatus } from '../../models/new/shot';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { ImageGalleryComponent } from 'src/app/shared/components/image-gallery/image-gallery.component';
import { ImagePreviewModalComponent } from 'src/app/components/photographer/components/photographer-order-detail/photographer-order-detail-shots/image-preview-modal/image-preview-modal.component';
import { UploadMultipleModalComponent } from 'src/app/components/photographer/components/photographer-order-detail/photographer-order-detail-shots/upload-multiple-modal/upload-multiple-modal.component';
import { UploadedImageShot } from 'src/app/components/photographer/models/new/imageShot';
import { ExcelService } from 'src/app/services/excel.service';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { ShotStatusEnum } from 'src/app/shared/types/shot-status.enum';
import { Paginable } from 'src/app/shared/types/paginable';
import { PaginationFilters } from '../../models/new/paginationFilters';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { ResumeMapper } from '../../services/resume-mapper';
import { ShotResume } from '../../models/new/shotResume';
import { AccordionService } from 'src/app/services/accordion.service';
import { UploadMultipleWithOrchestratorModalComponent } from 'src/app/components/photographer/components/photographer-order-detail/photographer-order-detail-shots/upload-multiple-with-orchestrator-modal/upload-multiple-with-orchestrator-modal.component';
import { UploadValidationResultsModalComponent } from 'src/app/components/photographer/components/photographer-order-detail/photographer-order-detail-shots/upload-multiple-with-orchestrator-modal/upload-validation-results-modal/upload-validation-results-modal.component';
import { OrderProgressModalComponent } from '../company-order-detail/company-order-detail-shots/order-progress-modal/order-progress-modal.component';

@Component({
  standalone: false,
  selector: 'bt-company-style-detail',
  templateUrl: './company-style-detail.component.html',
  styleUrls: ['./company-style-detail.component.scss']
})
export class CompanyStyleDetailComponent implements OnInit {
  style: Style | undefined = undefined;
  styleColorShots: Paginable<Selectable<ColorShots>> = new Paginable<Selectable<ColorShots>>();
  styleColorShotFilters = new PaginationFilters();
  styleStatus = StyleStatus;
  loading: boolean = false;
  shotApprovalStatus = ShotApprovalStatus;
  shotStatusEnum = ShotStatusEnum;
  shotResume: ShotResume = new ShotResume();
  shotImageLoadingState: Record<string, boolean> = {};
  acceptedExtensions = ['.jpg', '.jpeg', '.png', '.mkv', '.mp4'];

  constructor(
    private route: ActivatedRoute,
    private styleMapper: StyleMapper,
    private orderMapper: OrderMapper,
    private imageService: ImageService,
    private utilitiesService: UtilitiesService,
    private dialog: MatDialog,
    private commonService: CommonService,
    private excelService: ExcelService,
    private graphQlService: GraphQLService,
    private router: Router,
    private resumeMapper: ResumeMapper,
    private accordionService: AccordionService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      const modelId = params['modelId'];

      this.getStyleInfo(Number(modelId));
      this.getStyleResume(Number(modelId));
    });
  }

  getStyleInfo(modelId: number) {
    this.graphQlService
      .getData<any>(DatabaseQueries.GET_STYLES, {
        where: { modelId: { eq: modelId } },
        first: 1
      })
      .pipe(map((res: any) => this.styleMapper.toStyle(res.data.styles.nodes[0])))
      .subscribe({
        next: (data: Style) => {
          this.style = data;
          this.loading = false;

          if (this.style?.status?.id == StyleStatus.Shooting) {
            this.dialog.open(OrderProgressModalComponent);
          }

          //aggiungi i dettagli
          this.getStyleDetails();
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error fetching style', SnackbarType.Error);
          this.loading = false;
          console.error(err);
        }
      });
  }

  getStyleDetails() {
    this.loading = true;

    this.route.queryParams.subscribe((params: Params) => {
      this.styleColorShotFilters = new PaginationFilters();
      this.styleColorShotFilters.page = params['page'] ? Number(params['page']) : 1;
      this.styleColorShotFilters.size = params['size'] ? Number(params['size']) : 20;

      this.styleColorShots.currentPage = this.styleColorShotFilters.page || 1;
      this.styleColorShots.size = this.styleColorShotFilters.size || 20;

      const pagination = this.styleColorShots.buildQuery(this.styleColorShots.currentPage);

      this.graphQlService
        .getData<any>(DatabaseQueries.GET_STYLE_DETAILS, { modelId: this.style?.modelId, ...pagination })
        .pipe(
          map((res: any) => {
            const colorShots = res.data.styleDetails;

            const paginable = new Paginable<Selectable<ColorShots>>({
              items: colorShots.nodes.map((csd: any) => {
                return {
                  color: new Color({ id: csd.colorId, name: csd.colorName, default: csd.defaultColor }),
                  shots: csd.colorsSizeGroups[0].shots.map((s: any) => this.orderMapper.toShot(s)),
                  selected: false,
                  opened: true
                };
              })
            });

            paginable.totalCount = colorShots.totalCount;
            paginable.currentPage = this.styleColorShotFilters.page || 1;
            paginable.size = this.styleColorShotFilters.size || 2;

            return paginable;
          })
        )
        .subscribe({
          next: (data: Paginable<Selectable<ColorShots>>) => {
            this.loading = false;

            this.styleColorShots = data;
            this.initializeShotImageLoadingStates();
          },
          error: (err) => {
            this.loading = false;
            this.utilitiesService.showSnackbar('Error loading style details', SnackbarType.Error);
          }
        });
    });
  }

  pageEvent(event: { pageNumber: number; pageSize: number }) {
    this.styleColorShots.currentPage = event.pageNumber;
    this.styleColorShots.size = event.pageSize;

    this.styleColorShotFilters.page = event.pageNumber;
    this.styleColorShotFilters.size = event.pageSize;

    this.refreshNavigation();
  }

  refreshNavigation() {
    this.loading = true;

    this.router.navigate([location.pathname], {
      queryParams: { page: this.styleColorShotFilters.page, size: this.styleColorShotFilters.size }
    });
  }

  getShotsWithImages(colorShots: ColorShots): Shot[] {
    return colorShots.shots?.filter((s) => s.imgPath) || [];
  }

  getIds(shots: Shot[]): number[] {
    return shots.map((s) => s.id!);
  }

  downloadAllShots(): void {
    this.loading = true;
    if (this.style?.modelId) {
      this.imageService.DownloadImagesByModelIds([this.style.modelId]).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          window.open(url, '_blank');
          this.utilitiesService.showSnackbar('Download started', SnackbarType.Success);
        },
        error: (err) => {
          this.loading = false;
          //this.utilitiesService.showSnackbar('Failed to start download', SnackbarType.Error);
        }
      });
    }
  }

  downloadShots(shotIds: number[]): void {
    this.loading = true;
    this.imageService.downloadImagesByImageIds(shotIds).subscribe({
      next: (res: Blob) => {
        this.loading = false;
        const url = window.URL.createObjectURL(res);
        window.open(url, '_blank');
        this.utilitiesService.showSnackbar('Download started', SnackbarType.Success);
      },
      error: (err) => {
        this.loading = false;
        //this.utilitiesService.showSnackbar('Failed to start download', SnackbarType.Error);
      }
    });
  }

  viewGallery(shots: Shot[], first?: number /*id del primo shot da visualizzare */): void {
    this.dialog
      .open(ImageGalleryComponent, {
        data: { shots: shots, first: first }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          // handle result if needed
        }
      });
  }

  removeAllShots() {
    if (!this.style?.modelId) return;

    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Clear Shots',
          message: 'Are you sure? This action will remove ALL images.'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.loading = true;
          this.graphQlService
            .getData(DatabaseQueries.DELETE_IMAGES_BY_MODEL_ID, { ModelId: this.style?.modelId })

            .subscribe({
              next: (data: any) => {
                this.utilitiesService.showSnackbar('Shots removed successfully', SnackbarType.Success);
                this.loading = false;
                this.ngOnInit();
              },
              error: (err) => {
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during removing shots',
                  SnackbarType.Error
                );

                this.loading = false;
              }
            });
        }
      });
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(
      () => {
        this.utilitiesService.showSnackbar('Filename copied to clipboard', SnackbarType.Success);
      },
      (err) => {
        this.utilitiesService.showSnackbar('Failed to copy filename', SnackbarType.Error);
      }
    );
  }

  setDefaultColor(colorId: number) {
    this.graphQlService
      .postData(MutationQuery.SET_DEFAULT_COLOR, {
        setDefaultColorInput: { modelId: this.style?.modelId, colorId: colorId }
      })

      .subscribe({
        next: (res: any) => {
          if (res.data.setDefaultColor.success) {
            this.utilitiesService.showSnackbar('Default color set successfully', SnackbarType.Success);
            this.getStyleDetails();
          }
        },
        error: (err) => this.utilitiesService.showSnackbar('Error setting default color', SnackbarType.Error)
      });
  }

  onFilesSelected(event: Event, shot: Shot, color: Color) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;
    const validFiles = Array.from(input.files).filter((file) => {
      const name = file.name.toLowerCase();
      return this.acceptedExtensions.some((ext) => name.endsWith(ext));
    });

    if (!validFiles.length) {
      this.utilitiesService.showSnackbar('Only JPG, PNG, MP4 and MKV files are allowed', SnackbarType.Info);
      return;
    }

    const previews: string[] = [];

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        previews.push(reader.result as string);

        // quando ho tutte le preview apro la modale
        if (previews.length === validFiles.length) {
          this.openPreviewModal(validFiles, previews, shot, color);
        }
      };
      reader.readAsDataURL(file);
    });
  }

  openPreviewModal(files: File[], previews: string[], shot: Shot, color?: Color) {
    this.dialog
      .open(ImagePreviewModalComponent, {
        data: { file: files[0], preview: previews[0] },
        width: '500px'
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.uploadSingleShot(files, shot, color);
        }
      });
  }

  uploadSingleShot(files: File[], shot: Shot, color?: Color) {
    this.loading = true;

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    let shotData = {
      aspectRatioId: shot?.aspectRatio?.id,
      codifiedUseId: shot?.codifiedUse?.id,
      mediaFormatId: shot?.mediaFormat?.id,
      colorId: color?.id,
      modelId: this.style?.modelId,
      shotId: null,
      shotTypeId: shot?.shotType?.id
      //sizeGroupId: colorSizeGroup?.sizeGroup.id
    };

    formData.append('shots', JSON.stringify([shotData]));

    this.upload(shot, formData);
  }

  upload(shot: Shot, formData: FormData) {
    this.commonService.uploadFiles(formData, 'company').subscribe({
      next: (res) => {
        this.loading = false;

        this.utilitiesService.showSnackbar('Shot uploaded successfully', SnackbarType.Success);
        this.ngOnInit();
      },
      error: (err) => {
        this.loading = false;
        //this.utilitiesService.showSnackbar(err.networkError.error.errors[0].message || 'Error during shot upload', SnackbarType.Error);
      }
    });
  }

  /*exportExcel() {
		this.loading = true;
		if (this.style?.modelId) {
			this.loading = true;
			this.excelService.getImageList(this.style?.modelId).subscribe({
				next: (res: Blob) => {
					this.loading = false;
					const url = window.URL.createObjectURL(res);
					const a = document.createElement('a');
					a.href = url;
					a.download = `Images_model_${this.style?.modelId}.xlsx`;
					a.click();
					window.URL.revokeObjectURL(url);
					this.utilitiesService.showSnackbar('Excel exported successfully', SnackbarType.Success);
				},
				error: (err) => {
					this.loading = false;
					//this.utilitiesService.showSnackbar('Failed to export excel', SnackbarType.Error);
				}
			});
		}
	}*/

  prepareUploadMultipleWithOrchestrator() {
    this.loading = true;

    let estimatedCodesExamples: string[] = [];

    this.styleColorShots.items.forEach((a) => {
      a.shots?.forEach((b) => {
        if (b.estimatedFileName) estimatedCodesExamples.push(b.estimatedFileName);
      });
    });

    this.loading = false;
    this.dialog
      .open(UploadMultipleWithOrchestratorModalComponent, {
        data: {
          style: this.style,
          estimatedCodes: estimatedCodesExamples
        }
      })
      .afterClosed()
      .subscribe((res: any) => {
        if (res) {
          this.dialog
            .open(UploadValidationResultsModalComponent, {
              data: {
                instanceId: res?.instanceId
              }
            })
            .afterClosed()
            .subscribe(() => {
              this.getStyleInfo(Number(this.style?.modelId));
              this.getStyleResume(Number(this.style?.modelId));

              this.utilitiesService.showSnackbar('Upload completed', SnackbarType.Success);
            });
        }
      });
  }

  findShotByEstimatedFileName(fileName: string): { colorId?: number; modelId?: number; shot?: Shot } | null {
    const normalize = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .replace(/\.jpeg$/i, '.jpg');

    const normalizedFileName = normalize(fileName);

    for (let csg of this.styleColorShots.items) {
      for (let s of csg.shots || []) {
        if (normalize(s.estimatedFileName || '') === normalizedFileName) {
          return {
            colorId: csg.color?.id,
            modelId: this.style?.modelId,
            shot: s
          };
        }
      }
    }

    return null;
  }

  getNotUploadedShots(colorShots: ColorShots) {
    return colorShots.shots?.filter((s) => s.shotApprovalStatus != ShotApprovalStatus.Approved);
  }

  getUploadedShots(colorShots: ColorShots) {
    return colorShots.shots?.filter((s) => s.shotApprovalStatus == ShotApprovalStatus.Approved);
  }

  getStyleResume(_modelId: number) {
    this.graphQlService
      .getData(OrderQueries.GET_STYLE_RESUME, { modelId: _modelId })
      .pipe(map((res: any) => this.resumeMapper.toShotResume(res.data.styleShotsResume)))
      .subscribe({
        next: (data: ShotResume) => {
          console.log('order resume', data);
          this.shotResume = data;
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error during fetching order resume', SnackbarType.Error);
          console.error(err);
        }
      });
  }

  thereAreImages(): boolean {
    return this.shotResume.uploadedShots > 0 || this.shotResume.approvedShots > 0 || this.shotResume.rejectedShots > 0;
  }

  toggleColorShots(colorShots: Selectable<ColorShots>) {
    colorShots.opened = !colorShots.opened;
    this.accordionService.companyStyleDetailsState.set(colorShots.color?.id!, colorShots.opened);
  }

  initializeShotImageLoadingStates() {
    const nextState: Record<string, boolean> = {};

    this.styleColorShots.items.forEach((item) => {
      item.shots?.forEach((shot) => {
        const key = this.getShotImageKey(shot);

        if (!key || !shot.imgPath) return;

        nextState[key] = this.shotImageLoadingState[key] ?? true;
      });
    });

    this.shotImageLoadingState = nextState;
  }

  getShotImageKey(shot: Shot): string {
    if (shot.id != undefined && shot.id != null) {
      return 'shot-' + shot.id;
    }

    return shot.imgPath || '';
  }

  isShotImageLoading(shot: Shot): boolean {
    if (!shot.imgPath) return false;

    const key = this.getShotImageKey(shot);
    return key ? this.shotImageLoadingState[key] !== false : false;
  }

  onShotImageLoad(shot: Shot): void {
    const key = this.getShotImageKey(shot);
    if (!key) return;

    this.shotImageLoadingState[key] = false;
  }

  onShotImageError(shot: Shot): void {
    const key = this.getShotImageKey(shot);
    if (!key) return;

    this.shotImageLoadingState[key] = false;
  }

  isVideo(shot: Shot): boolean {
    if (shot.imgPath) {
      const path = shot.imgPath.toLowerCase();
      if (path.lastIndexOf('.mp4') > 0 || path.lastIndexOf('.mkv') > 0) return true;
    }
    return false;
  }
}
